import React, { memo } from 'react';
import { useSelector } from 'react-redux';
import { Box, Chip, Paper, Button, Checkbox, TextField, Autocomplete } from '@mui/material';
import { CheckBoxOutlineBlank, CheckBox } from '@mui/icons-material';
import MuiDatePicker from '../../../../../../../components/FormFields/MuiDatePicker';
import MuiTimePicker from '../../../../../../../components/FormFields/MuiTimePicker';
import {
    autoCompleteInputStyles,
    customListBox,
    listBoxItem,
    submitAndSelectAll
} from '../../../../../../../styles/mui-styles/autoCompleteInput';
import { H5 } from '../../../../../../../themes/typography';

const icon = <CheckBoxOutlineBlank fontSize="small" />;
const checkedIcon = <CheckBox fontSize="small" color="fourth" />;

// Submission section
const SubmitSection = memo(function SubmitSection(props) {
    return (
        <Paper {...props}>
            {props.children}
            <Box sx={submitAndSelectAll}>
                <Button onClick={props.selectAllClick} variant="text">
                    {!props.selectText ? 'Select All' : 'Clear All'}
                </Button>
                <Button onClick={props.submitClick} variant="tertiary">
                    Submit
                </Button>
            </Box>
        </Paper>
    );
});

export default function TicketScheduling({
    formik,
    isEditModeOn,
    mappedDates,
    mappedSlots,
    showDatesInInput,
    isSelectAllDates,
    isDateDropdownOpen,
    minStartTime,
    maxStartTime,
    minEndTime,
    maxEndTime,
    maxRangeDate,
    minRangeDate,
    handleDateSubmission,
    handleSlotSubmission,
    handleDateChange,
    handleSlotChange,
    onDateDropdownOpen,
    onSlotDropdownOpen,
    handleAllDatesSelect,
    handleAllSlotsSelect,
    preventBackSpaceKey,
    setShowDatesInInput,
    setIsSelectAllDates,
    setIsDateDropdownOpen,
    setShowSlotsInInput,
    setIsSelectAllSlots,
    isSelectAllSlots,
    isSlotDropdownOpen,
    handleDateDropdownClose,
    handleSlotDropdownClose,
    showSlotsInInput
}) {
    const { eventData } = useSelector((state) => state.event);

    // render selected mappedDates in input field conditionally
    function renderSelectedDates(value, getTagProps) {
        if (value.length === mappedDates?.length) {
            return (
                <Chip
                    disabled={!isEditModeOn}
                    label="All dates are selected"
                    onDelete={() => {
                        formik.setFieldValue('ticketValidDates', []);
                        formik.setFieldValue('ticketValidSlots', []);
                        formik.setFieldValue('scheduleIds', []);
                        setIsSelectAllDates(false);
                        setShowDatesInInput(false);
                    }}
                />
            );
        }
        return value.map((option, index) => (
            <Chip label={option?.date} key={option} {...getTagProps({ index })} />
        ));
    }

    // Render selected slots in input field conditionally
    function renderSelectedSlots(value, getTagProps) {
        if (value.length === mappedSlots?.length) {
            return (
                <Chip
                    disabled={!isEditModeOn}
                    label="All slots are selected"
                    onDelete={() => {
                        formik.setFieldValue('ticketValidSlots', []);
                        setIsSelectAllSlots(false);
                        setShowSlotsInInput(false);
                    }}
                />
            );
        }
        return value.map((option, index) => (
            <Chip
                label={`${option.from_time} to ${option.to_time}`}
                key={option?.id}
                {...getTagProps({ index })}
            />
        ));
    }

    return (
        <React.Fragment>
            <div className="ticket-valid-for-container">
                {/* Ticket valid for */}
                <div className="ticket-valid">
                    <H5>Ticket valid for</H5>

                    <div className="multi-select-option">
                        <Autocomplete
                            multiple
                            fullWidth
                            disableCloseOnSelect
                            id="validDatesSelection"
                            options={mappedDates}
                            value={formik.values.ticketValidDates}
                            onChange={handleDateChange}
                            onOpen={onDateDropdownOpen}
                            onKeyDown={preventBackSpaceKey}
                            open={isDateDropdownOpen}
                            onClose={handleDateDropdownClose}
                            renderTags={renderSelectedDates}
                            slots={{ listbox: SubmitSection }}
                            disabled={!isEditModeOn}
                            slotProps={{
                                listbox: {
                                    submitClick: handleDateSubmission,
                                    selectAllClick: handleAllDatesSelect,
                                    selectText: isSelectAllDates
                                },
                                paper: { sx: { padding: '0.5rem' } }
                            }}
                            // renderOption={(props, option, { selected }) => {
                            //     const { key, ...optionProps } = props;
                            //     return (
                            //         <React.Fragment key={option?.id}>
                            //             <div
                            //                 style={{
                            //                     ...customListBox,
                            //                     backgroundColor: selected ? 'rgba(0, 0, 0, 0.08)' : 'transparent'
                            //                 }}
                            //             >
                            //                 <li style={listBoxItem} {...optionProps}>
                            //                     <Checkbox
                            //                         icon={icon}
                            //                         checkedIcon={checkedIcon}
                            //                         style={{ marginRight: 8 }}
                            //                         checked={selected}
                            //                     />
                            //                     {option?.date}
                            //                 </li>
                            //             </div>
                            //         </React.Fragment>
                            //     );
                            // }}
                            renderOption={(props, option, { selected }) => {
                                const { key, ...optionProps } = props;

                                // Render only the "continuous" event type section
                                if (eventData?.event_type === 'continuous') {
                                    if (option.id === mappedDates[0]?.id) {
                                        return (
                                            <React.Fragment key="continuous-range">
                                                <div
                                                    style={{
                                                        ...customListBox,
                                                        backgroundColor: selected
                                                            ? 'rgba(0, 0, 0, 0.08)'
                                                            : 'transparent'
                                                    }}
                                                >
                                                    <li style={listBoxItem} {...optionProps}>
                                                        <Checkbox
                                                            icon={icon}
                                                            checkedIcon={checkedIcon}
                                                            style={{ marginRight: 8 }}
                                                            checked={selected}
                                                        />
                                                        {`${mappedDates[0]?.date} to ${mappedDates[mappedDates.length - 1]?.date}`}
                                                    </li>
                                                </div>
                                            </React.Fragment>
                                        );
                                    }
                                    return null; // Skip rendering other options for "continuous" event type
                                }

                                // Render other event types normally
                                return (
                                    <React.Fragment key={option?.id}>
                                        <div
                                            style={{
                                                ...customListBox,
                                                backgroundColor: selected
                                                    ? 'rgba(0, 0, 0, 0.08)'
                                                    : 'transparent'
                                            }}
                                        >
                                            <li style={listBoxItem} {...optionProps}>
                                                <Checkbox
                                                    icon={icon}
                                                    checkedIcon={checkedIcon}
                                                    style={{ marginRight: 8 }}
                                                    checked={selected}
                                                />
                                                {option?.date}
                                            </li>
                                        </div>
                                    </React.Fragment>
                                );
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Select Dates"
                                    sx={autoCompleteInputStyles}
                                    error={
                                        formik.touched.ticketValidDates &&
                                        Boolean(formik.errors.ticketValidDates)
                                    }
                                    helperText={
                                        formik.touched.ticketValidDates &&
                                        formik.errors.ticketValidDates
                                    }
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: showDatesInInput
                                            ? params.InputProps.startAdornment
                                            : null
                                    }}
                                />
                            )}
                        />
                    </div>

                    {!isDateDropdownOpen &&
                        formik.values.ticketValidDates.length === 1 &&
                        mappedSlots?.length > 0 && (
                            <div className="multi-select-option">
                                <Autocomplete
                                    open={isSlotDropdownOpen}
                                    onOpen={onSlotDropdownOpen}
                                    onClose={handleSlotDropdownClose}
                                    multiple
                                    fullWidth
                                    disableCloseOnSelect
                                    id="validSlotsSelection"
                                    value={formik.values.ticketValidSlots}
                                    options={mappedSlots}
                                    onKeyDown={preventBackSpaceKey}
                                    getOptionLabel={(option) =>
                                        `${option.from_time} to ${option.to_time}`
                                    }
                                    onChange={handleSlotChange}
                                    renderTags={renderSelectedSlots}
                                    disabled={!isEditModeOn}
                                    slots={{ listbox: SubmitSection }}
                                    slotProps={{
                                        listbox: {
                                            submitClick: handleSlotSubmission,
                                            selectAllClick: handleAllSlotsSelect,
                                            selectText: isSelectAllSlots
                                        },
                                        paper: { sx: { padding: '0.5rem' } }
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Select slots"
                                            sx={autoCompleteInputStyles}
                                            error={
                                                formik.touched.ticketValidSlots &&
                                                Boolean(formik.errors.ticketValidSlots)
                                            }
                                            helperText={
                                                formik.touched.ticketValidSlots &&
                                                formik.errors.ticketValidSlots
                                            }
                                            InputProps={{
                                                ...params.InputProps,
                                                startAdornment: showSlotsInInput
                                                    ? params.InputProps.startAdornment
                                                    : null
                                            }}
                                        />
                                    )}
                                    renderOption={(props, option, { selected }) => {
                                        const { key, ...optionProps } = props;
                                        return (
                                            <React.Fragment key={key}>
                                                <div
                                                    style={{
                                                        ...customListBox,
                                                        backgroundColor: selected
                                                            ? 'rgba(0, 0, 0, 0.08)'
                                                            : 'transparent'
                                                    }}
                                                >
                                                    <li style={listBoxItem} {...optionProps}>
                                                        <Checkbox
                                                            icon={icon}
                                                            checkedIcon={checkedIcon}
                                                            style={{ marginRight: 8 }}
                                                            checked={selected}
                                                        />
                                                        {`${option.from_time} to ${option.to_time}`}
                                                    </li>
                                                </div>
                                            </React.Fragment>
                                        );
                                    }}
                                />
                            </div>
                        )}
                </div>

                {/* Sale Schedule */}
                <div className="ticket-valid-for-container">
                    <H5>Sale Schedule</H5>
                    <div className="ticket-valid-for-container">
                        <div style={{ alignSelf: 'stretch', display: 'flex', gap: '1rem' }}>
                            <MuiDatePicker
                                id="startDate"
                                name="startDate"
                                label="Start date"
                                value={formik.values.saleStartDate || ''}
                                onOpen={() => formik.setFieldValue('maxDate', maxRangeDate)}
                                maxDate={maxRangeDate}
                                disabled={!isEditModeOn}
                                onChange={(date) => formik.setFieldValue('saleStartDate', date)}
                                error={
                                    formik.touched.saleStartDate &&
                                    Boolean(formik.errors.saleStartDate)
                                }
                                helperText={
                                    formik.touched.saleStartDate && formik.errors.saleStartDate
                                }
                            />
                            <MuiTimePicker
                                id="startTime"
                                name="startTime"
                                label="Start time"
                                minTime={minStartTime}
                                maxTime={maxStartTime}
                                value={formik.values.saleStartTime || ''}
                                disabled={!isEditModeOn}
                                onChange={(time) => formik.setFieldValue('saleStartTime', time)}
                                error={
                                    formik.touched.saleStartTime &&
                                    Boolean(formik.errors.saleStartTime)
                                }
                                helperText={
                                    formik.touched.saleStartTime && formik.errors.saleStartTime
                                }
                            />
                            <MuiDatePicker
                                id="endDate"
                                name="endDate"
                                label="End date"
                                value={formik.values.saleEndDate || ''}
                                onOpen={() => formik.setFieldValue('maxDate', maxRangeDate)}
                                maxDate={maxRangeDate}
                                minDate={minRangeDate}
                                disabled={!isEditModeOn}
                                onChange={(date) => formik.setFieldValue('saleEndDate', date)}
                                error={
                                    formik.touched.saleEndDate && Boolean(formik.errors.saleEndDate)
                                }
                                helperText={formik.touched.saleEndDate && formik.errors.saleEndDate}
                            />
                            <MuiTimePicker
                                id="endTime"
                                name="endTime"
                                label="End time"
                                minTime={minEndTime}
                                maxTime={maxEndTime}
                                disabled={!isEditModeOn}
                                value={formik.values.saleEndTime || ''}
                                onChange={(time) => formik.setFieldValue('saleEndTime', time)}
                                error={
                                    formik.touched.saleEndTime && Boolean(formik.errors.saleEndTime)
                                }
                                helperText={formik.touched.saleEndTime && formik.errors.saleEndTime}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
}
